/**
 * The academy admin API — the tenant-scoped, server-paginated surface at
 * `/api/pathways/v1/academy/…`, described in the backend's own `academy.py`.
 *
 * THIS REPLACES FOUR FAN-OUTS, MEASURED BEFORE AND AFTER:
 *
 *     screen                requests before   requests after
 *     /admin/enrolments      4-8 (chunked, plus the whole catalogue)   1
 *     /admin/grades          25 (17 of them the SAME endpoint)         1
 *     /admin/certificates    34 (17 per course + 12 per holder)        1
 *     /admin/progress        up to 60, one per learner per course      1
 *
 * Every screen also shares ONE more request for the course catalogue, which itself
 * replaces `/api/contentstore/v2/home/courses?page_size=500` — a call that ignored its
 * own `org` filter and returned the platform's entire catalogue (110 courses, five orgs)
 * to every tenant regardless of size.
 *
 * ALL FIVE SHARE ONE ENVELOPE AND ONE SCOPE, both decided on the SERVER, not here:
 *
 *   - the tenant boundary is the caller's own orgs (`writable_orgs`), never a value this
 *     file sends — there is no `org` parameter on any of these for exactly that reason;
 *   - `course_id` on the four learner-data endpoints NARROWS an already-scoped read; a
 *     course outside the academy is a 404, indistinguishable from one that does not
 *     exist, so this file never needs to pre-check a course before asking about it;
 *   - `page_size` defaults to 50 and is clamped to 200 server-side. Every caller below
 *     asks for 200 outright: the point of these endpoints is ONE request per screen, and
 *     a screen that then had to decide whether to ask for page 2 would still be making a
 *     second request some of the time. 200 covers every academy measured so far (the
 *     largest owns ~110 courses); `count` is still returned so a screen CAN say "showing
 *     200 of 340" rather than silently implying it has everything, the same discipline
 *     the fan-outs it replaces were missing.
 */

import { openedx } from './openedx';

const BASE = '/api/pathways/v1/academy';

/** The one envelope every route below answers with. `results` is the page's rows. */
export interface AcademyPage<T> {
  count: number;
  page: number;
  page_size: number;
  num_pages: number;
  results: T[];
}

interface PageParams {
  page?: number;
  pageSize?: number;
}

/** `?key=value&…`, dropping anything unset — an omitted param is not the same as `''`. */
function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const built = search.toString();
  return built ? `?${built}` : '';
}

/* ------------------------------------------------------------------- courses ---- */

/**
 * One course in the academy's catalogue — its own, or licensed to it.
 *
 * `has_learners` and `owned` are both computed server-side so no screen has to fetch
 * enrolments (or a licence list) just to decide what to offer in a picker. `owned`
 * distinguishes a course this tenant may manage from one it may only show — see
 * `academy.py::read_courses` for why the two are not the same question.
 */
export interface AcademyCourse {
  course_key: string;
  display_name: string;
  org: string;
  number: string;
  run: string;
  start: string | null;
  end: string | null;
  has_learners: boolean;
  owned: boolean;
}

/**
 * The academy's catalogue: its own courses plus the ones licensed to it.
 *
 * `courseId` NARROWS to one course, and it is listed here because until recently it did
 * not: `read_courses` was the one academy reader that never called a resolver, so the
 * parameter was accepted and silently ignored — naming any single course returned the whole
 * catalogue with a 200. It now behaves like `courseId` on the four readers below, which is
 * why it is spelled the same way.
 */
export const listAcademyCourses = (
  params: PageParams & { search?: string; courseId?: string } = {},
) =>
  openedx.get<AcademyPage<AcademyCourse>>(
    `${BASE}/courses${query({
      page: params.page,
      page_size: params.pageSize,
      search: params.search,
      course_id: params.courseId,
    })}`,
  );

/* ---------------------------------------------------------------- enrolments ---- */

/** One enrolment, already joined to the person and the course — see `read_enrolments`. */
export interface AcademyEnrolment {
  user: string;
  username: string;
  user_id: number;
  email: string;
  name: string;
  course_id: string;
  course_title: string;
  mode: string;
  is_active: boolean;
  created: string | null;
}

export const listAcademyEnrolments = (
  params: PageParams & { courseId?: string; activeOnly?: boolean } = {},
) =>
  openedx.get<AcademyPage<AcademyEnrolment>>(
    `${BASE}/enrolments${query({
      page: params.page,
      page_size: params.pageSize,
      course_id: params.courseId,
      active_only: params.activeOnly ? 'true' : undefined,
    })}`,
  );

/* -------------------------------------------------------------------- grades ---- */

/** One assignment's contribution to a learner's grade, from the course's own summary. */
export interface AcademyGradeSection {
  percent: number;
  label: string;
  detail: string;
  category: string;
  prominent?: boolean;
  sequential_id: string | null;
}

/**
 * One learner's standing in one course, paged by ENROLMENT rather than by course.
 *
 * `current_grade` is `int(percent * 100)`, ALREADY A PERCENTAGE AND TRUNCATED — 99.6%
 * arrives as 99 — copied field-for-field from the per-course endpoint this replaces so a
 * mismatch would mean a bug rather than a reinterpretation. `reason` is set instead of
 * the row being silently dropped when the platform cannot grade the enrolment at all
 * (a course with no gradeable content, for instance); the old per-course fan-out had no
 * equivalent because upstream's own endpoint drops those learners without a word.
 *
 * `section_breakdown` IS EMPTY UNLESS ONE COURSE WAS NAMED, and `has_breakdown` says which
 * kind of row this is. That is not a shortcut, it is the difference between a 2.6s page and
 * a 12s one: building the breakdown means `CourseGrade.summary`, which forces the full
 * per-learner grading computation against the course's block structure. MEASURED on the
 * same 37 enrolments across 17 courses — 2.60s without it, 31.69s with it. The
 * academy-wide table shows one grade per learner and never renders the per-assignment
 * detail, so it asks without; the drill-in passes `courseId` and gets it in full.
 *
 * READ `has_breakdown` RATHER THAN `section_breakdown.length` before concluding anything
 * about a course. An empty list means "not computed here" on the overview and "this course
 * grades nothing" on the drill-in, and those are opposite facts.
 */
export interface AcademyGrade {
  course_id: string;
  course_title: string;
  username: string;
  passed: boolean;
  current_grade: number;
  section_breakdown: AcademyGradeSection[];
  /** False on the academy-wide list, true when `courseId` named one course. */
  has_breakdown: boolean;
  reason?: string;
}

export const listAcademyGrades = (params: PageParams & { courseId?: string } = {}) =>
  openedx.get<AcademyPage<AcademyGrade>>(
    `${BASE}/grades${query({ page: params.page, page_size: params.pageSize, course_id: params.courseId })}`,
  );

/* ----------------------------------------------------------------- certificates ---- */

/**
 * One issued certificate, with everything the two endpoints this replaces answered
 * between them — see `academy.py::read_certificates` for why one table has both halves.
 *
 * `grade` is a float AS A STRING, e.g. `"1.0"` — the platform's own representation.
 * `download_url` is null until the certificate is actually generated, not merely stored.
 * UNLIKE THE OLD PER-HOLDER JOIN, every row here carries its real status even when the
 * platform would otherwise hide a held-back certificate from the holder — see the
 * backend module docstring for why that is a correction, not a leak.
 */
export interface AcademyCertificate {
  username: string;
  email: string;
  name: string;
  course_id: string;
  course_title: string;
  certificate_status: string;
  enrollment_track: string;
  mode: string;
  grade: string;
  created_date: string | null;
  modified_date: string | null;
  download_url: string | null;
  uuid: string;
  invalidated_by: string | null;
}

export const listAcademyCertificates = (params: PageParams & { courseId?: string } = {}) =>
  openedx.get<AcademyPage<AcademyCertificate>>(
    `${BASE}/certificates${query({ page: params.page, page_size: params.pageSize, course_id: params.courseId })}`,
  );

/* --------------------------------------------------------------------- progress ---- */

/**
 * How far one learner has got through one course.
 *
 * `percent` IS 0.0-1.0, not a percentage — matches `asPercent` from `instructor.ts`.
 * `earned`/`possible` are the aggregator's own completion quantities behind that
 * fraction. WHAT IS NOT HERE, DELIBERATELY: the old per-learner endpoint's
 * `completion_summary` (complete/incomplete/locked unit COUNTS) and its `course_grade`,
 * `section_scores` and `certificate_data` — those are a courseware-outline concept the
 * aggregator does not carry, and reproducing them here would mean going back to one
 * request per learner. The "Scores" drill-in in `AdminProgress` still reaches
 * `instructor.ts::getLearnerProgress` for exactly that detail, ON DEMAND for the one row
 * an admin opens, rather than eagerly for every row on the page.
 */
export interface AcademyProgress {
  username: string;
  user_id: number;
  course_id: string;
  course_title: string;
  percent: number;
  earned: number;
  possible: number;
  since: string | null;
}

export const listAcademyProgress = (params: PageParams & { courseId?: string } = {}) =>
  openedx.get<AcademyPage<AcademyProgress>>(
    `${BASE}/progress${query({ page: params.page, page_size: params.pageSize, course_id: params.courseId })}`,
  );
